import './styles.css'
import React from 'react'
import { debounce } from 'lodash'
import moment from 'moment'
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Message,
  Modal,
  Popup,
  Segment,
  Transition,
} from 'semantic-ui-react'
import KegCollectionAvailableDays from '../shared/KegCollectionAvailableDays/KegCollectionAvailableDays'
import KegCollectionKegQuantitiesInputs from '../shared/KegCollectionKegQuantitiesInputs'
import KegCollectionLocationBtnGroup from '../shared/KegCollectionLocationBtnGroup/KegCollectionLocationBtnGroup'
import KegCollectionPickupInstructions from '../shared/KegCollectionPickupInstructions/KegCollectionPickupInstructions'
import KegCollectionPhoneInput from '../shared/KegCollectionPhoneInput'
import KegCollectionTimesDropdownGroup from '../shared/KegCollectionTimesDropdownGroup'
import ModalErrorBanner from '../shared/BookingsModalErrorBanner/index'
import {
  HOURS_MIN,
  HOURS_MAX,
  isHour,
  toNum,
  numericInputRegex,
} from '../../utils/kegCollectionFormUtils'
import {
  CREATE_KEG_COLLECTION_REQUEST,
  FETCH_KEG_COLLECTION_BOOKING_VENUES,
  FETCH_KEG_COLLECTION_BOOKING_VENUE_COLLECTION_INFO,
  SHOW_BOOKINGS_MODAL_ERROR_MESSAGE,
  HIDE_BOOKINGS_MODAL_ERROR_MESSAGE,
  RESET_CREATE_KEG_COLLECTION_STATUS_STATE,
} from '../../../../../redux/actions/actionTypes'
import useKegCollectionErrorState from '../../utils/useKegCollectionErrorState'
import { sanitizedInput } from '../../../../../utils/formNormalization'

const initialFormState = {
  customerId: '',
  customerTapId: '',
  customerName: '',
  addressId: '',
  fullAddress: '',
  dateCreated: null,
  pickupInstruction: '',
  availableDays: {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
  },
  startHour: HOURS_MIN,
  endHour: HOURS_MAX,
  kegLocation: 'Inside',
  updateVenueCollectionInfo: false,
  kcRequestItemQtyObjectSet: [
    { containerType: 'L20', quantity: 0 },
    { containerType: 'L30', quantity: 0 },
    { containerType: 'L50', quantity: 0 },
    { containerType: 'G9', quantity: 0 },
  ],
  phoneNumber: '',
  L30: '',
  L20: '',
  L50: '',
  G9: '',
  createdBy: null,
}

const formatAddress = address => {
  if (!address) return ''
  const { address1, address2, address3, city, stateOrProvince, postalCode, country } = address
  let addressLabel = ''
  const addressParts = [address1, address2, address3, city, stateOrProvince, postalCode, country]
  for (let i = 0; i < addressParts.length; i += 1) {
    if (addressParts[i] && addressParts[i].trim().length > 0) {
      addressLabel += `${addressParts[i]}, `
    }
  }
  return addressLabel.slice(0, -2)
}

const KegCollectionCreateModal = props => {
  const {
    toggleModal,
    onClose,
    onOpen,
    translate,
    dispatch,
    createAction,
    selectedVenueCollectionInfo,
    venuesList,
    currentUserId,
    createKegCollectionRequestStatusState: { isCreating, didCreateSucceed, didCreateFail },
    getSubsidiaryIdsForRegion,
    region,
  } = props

  const [formValues, setFormValues] = React.useState(initialFormState)
  const [venueSearch, setVenueSearch] = React.useState('')
  const [selectedVenueId, setSelectedVenueId] = React.useState(null)
  const [selectedVenueSummary, setSelectedVenueSummary] = React.useState(null)
  const [mounted, setMounted] = React.useState(false)
  const { validationErrors } = useKegCollectionErrorState(formValues, true)
  const [phoneNumberCountryCode, setPhoneNumberCountryCode] = React.useState(undefined)
  const [showValidationErrors, setShowValidationErrors] = React.useState(false)

  const resetCreateFormState = React.useCallback(() => {
    setFormValues(initialFormState)
    setVenueSearch('')
    setSelectedVenueId(null)
    setSelectedVenueSummary(null)
    dispatch(createAction(HIDE_BOOKINGS_MODAL_ERROR_MESSAGE))
    dispatch(createAction(RESET_CREATE_KEG_COLLECTION_STATUS_STATE))
    setPhoneNumberCountryCode(undefined)
    setShowValidationErrors(false)
  }, [dispatch, createAction])

  React.useEffect(() => {
    // Reset form values each time the modal is opened
    if (!toggleModal) return
    resetCreateFormState()
  }, [resetCreateFormState, toggleModal])

  React.useEffect(() => {
    // Handle successful creation: reset form values and close modal
    if (didCreateSucceed) {
      resetCreateFormState()
      onClose()
    }
  }, [didCreateSucceed, dispatch, translate, createAction, resetCreateFormState, onClose])

  React.useEffect(() => {
    if (didCreateFail)
      dispatch(
        createAction(SHOW_BOOKINGS_MODAL_ERROR_MESSAGE, {
          header: translate('Error'),
          content: translate('kegCollectionCreateErrorMessage'),
          isError: true,
        })
      )
  }, [didCreateFail, dispatch, translate, createAction])

  React.useEffect(() => {
    // If the modal is closed, do nothing
    if (!toggleModal) return
    // Render venue name text & address text,
    // set formValues for customerId, customerTapId, and addressId, and full address
    // when a venue is selected
    const found = venuesList?.content?.find(v => String(v.id) === String(selectedVenueId))
    if (found) {
      const fullAddress = formatAddress(found.defaultShippingAddress)
      setPhoneNumberCountryCode(found?.defaultShippingAddress?.country || undefined)
      setFormValues(prev => ({
        ...prev,
        customerId: found.id || '',
        customerTapId: found.tapcustomerId || '',
        addressId: found.defaultShippingAddress?.id || '',
        fullAddress,
      }))
      setSelectedVenueSummary({
        name: found.name || translate('noNameOnFile'),
        address: fullAddress || translate('noAddressOnFile'),
      })
    }

    // Pre-populate collection info fields if available: pickup instructions,
    // available days, start/end hours, contact phone, & keg location
    if (selectedVenueCollectionInfo?.didFetchSucceed && selectedVenueCollectionInfo?.content?.id) {
      const {
        pickupInstruction,
        venueOpenMonday,
        venueOpenTuesday,
        venueOpenWednesday,
        venueOpenThursday,
        venueOpenFriday,
        startHour,
        endHour,
        kegLocation,
        phoneNumber,
      } = selectedVenueCollectionInfo.content

      setFormValues(prev => ({
        ...prev,
        pickupInstruction: pickupInstruction || '',
        availableDays: {
          monday: Boolean(venueOpenMonday),
          tuesday: Boolean(venueOpenTuesday),
          wednesday: Boolean(venueOpenWednesday),
          thursday: Boolean(venueOpenThursday),
          friday: Boolean(venueOpenFriday),
        },
        startHour: isHour(startHour) ? startHour : HOURS_MIN,
        endHour: isHour(endHour) ? endHour : HOURS_MAX,
        kegLocation: kegLocation || 'Inside',
        phoneNumber: phoneNumber || '',
      }))
    } else {
      setFormValues(prev => ({
        ...prev,
        updateVenueCollectionInfo: true,
      }))
    }
  }, [
    selectedVenueCollectionInfo,
    selectedVenueCollectionInfo?.content,
    venueSearch,
    selectedVenueId,
    venuesList?.content,
    translate,
    toggleModal,
  ])

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    // Handle mount animation
    if (toggleModal) {
      const t = setTimeout(() => setMounted(true), 80)
      return () => clearTimeout(t)
    }
    setMounted(false)
  }, [toggleModal])

  const venueOptions = React.useMemo(() => {
    const items = venuesList?.content ?? []
    return items
      .map(v => {
        const addressText = formatAddress(v.defaultShippingAddress) || translate('noAddressOnFile')
        return {
          key: String(v.id),
          value: String(v.id),
          text: `${v.name} (${addressText})`,
        }
      })
      .sort((a, b) => a.text.localeCompare(b.text))
  }, [venuesList?.content, translate])

  const debouncedSetVenueName = React.useMemo(
    () =>
      debounce(str => {
        dispatch(
          createAction(FETCH_KEG_COLLECTION_BOOKING_VENUES, {
            customerParams: {
              searchString: str,
              ct: 'VENUE',
              subsidiaryIds: getSubsidiaryIdsForRegion(region),
              sts: 'ACT',
              includeDSHP: true,
            },
          })
        )
      }, 700),
    [dispatch, createAction, getSubsidiaryIdsForRegion, region]
  )

  // Cleanup debounce on unmount
  React.useEffect(() => () => debouncedSetVenueName.cancel(), [debouncedSetVenueName])

  const hasErrors = Object.keys(validationErrors).length > 0
  const isVenueSelected = Boolean(selectedVenueId)
  const isFormComponentsDisabled = isCreating || venuesList.isFetching || !isVenueSelected
  const isVenueCollectionInfoMissing = !selectedVenueCollectionInfo?.content?.id && isVenueSelected

  const handleVenueSearchOnChange = React.useCallback(
    searchQuery => {
      if (searchQuery === '') {
        resetCreateFormState()
        debouncedSetVenueName.cancel()
      } else {
        setVenueSearch(searchQuery)
        setFormValues(prev => ({ ...prev, customerName: searchQuery }))
        if (searchQuery.length > 2) {
          if (searchQuery) debouncedSetVenueName(searchQuery)
        }
      }
    },
    [setVenueSearch, debouncedSetVenueName, resetCreateFormState, setFormValues]
  )

  const handleVenueOnChange = React.useCallback(
    value => {
      if (value === '') {
        resetCreateFormState()
        debouncedSetVenueName.cancel()
      } else {
        setSelectedVenueId(value)
        const found = venuesList.content?.find(v => String(v.id) === String(value))
        setVenueSearch(found?.name ?? '')
        setFormValues(prev => ({ ...prev, customerName: found?.name || '' }))
        dispatch(
          createAction(FETCH_KEG_COLLECTION_BOOKING_VENUE_COLLECTION_INFO, {
            venueId: value,
          })
        )
      }
    },
    [venuesList.content, dispatch, resetCreateFormState, createAction, debouncedSetVenueName]
  )

  const handleAvailableDaysChange = React.useCallback(
    (day, checked) => {
      setFormValues(prev => ({
        ...prev,
        availableDays: { ...prev.availableDays, [day]: !!checked },
      }))
    },
    [setFormValues]
  )

  const handleCollectionLocationChange = React.useCallback(
    location => {
      setFormValues(prev => ({ ...prev, kegLocation: location }))
    },
    [setFormValues]
  )

  const handleQtyChange = React.useCallback(event => {
    const { name, value } = event.target
    if (value.length > 3 || !numericInputRegex.test(value)) return
    const asNum = value === '' ? '' : toNum(value)
    setFormValues(prev => ({
      ...prev,
      [name]: asNum,
      kcRequestItemQtyObjectSet: prev.kcRequestItemQtyObjectSet.map(kc =>
        kc.containerType === name ? { ...kc, quantity: value === '' ? 0 : toNum(value) } : kc
      ),
    }))
  }, [])

  const handlePickupInstructionOnChange = React.useCallback(
    value => {
      setFormValues(prev => ({ ...prev, pickupInstruction: sanitizedInput(value) }))
    },
    [setFormValues]
  )

  const handlePhoneNumberChange = React.useCallback(
    value => {
      setFormValues(prev => ({ ...prev, phoneNumber: value || '' }))
    },
    [setFormValues]
  )

  const handleAvailableTimesChange = React.useCallback(
    (field, value) => {
      setFormValues(prev => ({ ...prev, [field]: toNum(value) }))
    },
    [setFormValues]
  )

  const handleCheckedUpdateCollectionInfo = React.useCallback(
    checked => {
      setFormValues(prev => ({ ...prev, updateVenueCollectionInfo: checked }))
    },
    [setFormValues]
  )

  const handleFormBtnCancel = React.useCallback(() => {
    onClose()
    resetCreateFormState()
  }, [onClose, resetCreateFormState])

  const handleCreateRequest = React.useCallback(() => {
    const {
      customerId,
      addressId,
      pickupInstruction,
      availableDays,
      startHour,
      endHour,
      kegLocation,
      fullAddress,
      customerName,
      customerTapId,
      kcRequestItemQtyObjectSet,
      phoneNumber,
      updateVenueCollectionInfo,
    } = formValues

    if (hasErrors) {
      setShowValidationErrors(true)
      return
    }

    const kegCollectionRequestAPIParams = {
      addressId,
      createdBy: currentUserId,
      customerId,
      customerName,
      customerTapId,
      dateCreated: moment().valueOf(),
      endHour,
      fullAddress,
      kcRequestItemQtyObjectSet,
      kegLocation,
      pickupInstruction,
      startHour,
      venueOpenMonday: availableDays.monday,
      venueOpenTuesday: availableDays.tuesday,
      venueOpenWednesday: availableDays.wednesday,
      venueOpenThursday: availableDays.thursday,
      venueOpenFriday: availableDays.friday,
      phoneNumber: phoneNumber || null,
      updateVenueCollectionInfo,
    }

    dispatch(createAction(CREATE_KEG_COLLECTION_REQUEST, { kegCollectionRequestAPIParams }))
  }, [dispatch, createAction, formValues, currentUserId, hasErrors])

  return (
    <>
      <Modal
        open={toggleModal}
        onClose={onClose}
        onOpen={onOpen}
        size="large"
        trigger={
          <Button color="green">
            <Icon name="plus" /> {translate('New Request')}
          </Button>
        }
        closeOnDimmerClick={false}
        closeOnEscape
      >
        <ModalErrorBanner />
        <Modal.Header>{translate('createKegCollectionModalHeader')}</Modal.Header>

        <Modal.Content scrolling>
          <Transition visible={mounted} animation="fade up" duration={300}>
            <Form autoComplete="off" error={showValidationErrors && hasErrors}>
              {showValidationErrors && hasErrors && (
                <Message
                  negative
                  icon="exclamation triangle"
                  header={translate('fixHighlightedFields')}
                  list={Object.values(validationErrors)}
                />
              )}

              <Grid stackable columns={2}>
                <Grid.Row>
                  <Grid.Column>
                    <Segment style={{ minHeight: 280 }}>
                      <Header
                        as="h4"
                        content={translate('Venue')}
                        subheader={translate('whereToCollectFrom')}
                      />
                      <Form.Field
                        required
                        error={showValidationErrors && !!validationErrors.customerName}
                      >
                        <label>{`${translate('Venue Name')} (${translate('kegCollectionFormVenueNameSubLabel')})`}</label>
                        <Dropdown
                          selection
                          search
                          fluid
                          clearable
                          options={venueOptions}
                          loading={venuesList.isFetching}
                          placeholder={
                            venuesList.isFetching
                              ? translate('loadingVenues')
                              : translate('enterVenueName')
                          }
                          noResultsMessage={
                            venuesList.isFetching
                              ? translate('loadingVenues')
                              : translate('noVenuesFound')
                          }
                          id="venueDropdown"
                          value={selectedVenueId}
                          searchQuery={venueSearch}
                          onSearchChange={(_, { searchQuery }) =>
                            handleVenueSearchOnChange(searchQuery)
                          }
                          onChange={(_, { value }) => handleVenueOnChange(value)}
                          disabled={isCreating}
                          autoFocus
                        />
                        {showValidationErrors && !!validationErrors.customerName && (
                          <Message negative size="small" content={validationErrors.customerName} />
                        )}
                        {venuesList.didFetchFail && (
                          <Message negative content={translate('venueFetchErrorMessage')} />
                        )}
                      </Form.Field>

                      {selectedVenueSummary && (
                        <>
                          <Header as="h4" content={translate('selectedVenueDetails')} />
                          <div id="createModalSelectedVenueDetailsTop">
                            <Icon name="building" style={{ marginRight: 8 }} />
                            <div>
                              <strong>{selectedVenueSummary?.name}</strong>
                            </div>
                          </div>
                          <div id="createModalSelectedVenueDetailsBottom">
                            <Icon name="map marker" style={{ marginRight: 8 }} />
                            <span>{selectedVenueSummary?.address}</span>
                          </div>
                        </>
                      )}
                    </Segment>
                  </Grid.Column>

                  <Grid.Column>
                    <Segment style={{ minHeight: 280 }}>
                      <Header
                        as="h4"
                        content={translate('kegQuantities')}
                        subheader={translate('enterKegQuantities')}
                      />
                      <KegCollectionKegQuantitiesInputs
                        formValues={formValues}
                        onChange={handleQtyChange}
                        isDisabled={isFormComponentsDisabled}
                        validationErrors={validationErrors}
                        showValidationErrors={showValidationErrors}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Segment style={{ minHeight: 280 }}>
                      <Header
                        as="h4"
                        content={translate('collectionWindow')}
                        subheader={translate('whenCanWeCollect')}
                      />

                      <Form.Field error={showValidationErrors && !!validationErrors.availableDays}>
                        <label>{translate('availableCollectionDays')}</label>
                        <KegCollectionAvailableDays
                          days={null}
                          formValues={formValues}
                          onClick={handleAvailableDaysChange}
                          isDisabled={isFormComponentsDisabled}
                          validationErrors={validationErrors}
                          showValidationErrors={showValidationErrors}
                        />
                      </Form.Field>

                      <Divider />

                      <KegCollectionTimesDropdownGroup
                        formValues={formValues}
                        onChange={handleAvailableTimesChange}
                        validationErrors={validationErrors}
                        isDisabled={isFormComponentsDisabled}
                      />

                    </Segment>
                  </Grid.Column>

                  <Grid.Column>
                    <Segment style={{ minHeight: 280 }}>
                      <h4 id="createModalKegLocationHeader">{translate('kegLocation')}</h4>
                      <div id="createModalWhereWillKegsBe">
                        <p>{translate('whereWillKegsBe')}</p>
                        <div>
                          <Popup
                            content={translate('whereWillKegsBeInfoTooltip')}
                            trigger={
                              <Icon
                                name="info circle"
                                aria-label={translate('whereWillKegsBeInfoTooltip')}
                              />
                            }
                          />
                        </div>
                      </div>
                      <KegCollectionLocationBtnGroup
                        onClick={handleCollectionLocationChange}
                        isDisabled={isFormComponentsDisabled}
                        formValues={formValues}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Segment style={{ minHeight: 230 }}>
                      <Header
                        as="h4"
                        content={translate('contactPhoneNumber')}
                        subheader={translate('contactPhoneNumberSubHeader')}
                      />
                      <KegCollectionPhoneInput
                        onChange={handlePhoneNumberChange}
                        formValues={formValues}
                        validationErrors={validationErrors}
                        isDisabled={isFormComponentsDisabled}
                        selectedCountry={phoneNumberCountryCode}
                      />
                    </Segment>
                  </Grid.Column>

                  <Grid.Column>
                    <Segment style={{ minHeight: 230 }}>
                      <Header
                        as="h4"
                        content={translate('pickupInstructions')}
                        subheader={translate('specialNotesForDrivers')}
                      />
                      <KegCollectionPickupInstructions
                        formValues={formValues}
                        onChange={handlePickupInstructionOnChange}
                        isDisabled={isFormComponentsDisabled}
                      />
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </Transition>
        </Modal.Content>

        <Modal.Actions>
          <Popup
            content={translate('updateVenueCollectionInfoTooltip')}
            trigger={
              <Icon name="info circle" aria-label={translate('updateVenueCollectionInfoTooltip')} />
            }
            disabled={isFormComponentsDisabled}
          />
          <Checkbox
            label={translate('updateVenueCollectionInfo')}
            checked={formValues.updateVenueCollectionInfo}
            onChange={(_, { checked }) => handleCheckedUpdateCollectionInfo(checked)}
            disabled={isFormComponentsDisabled || isVenueCollectionInfoMissing}
          />
          <Button onClick={handleFormBtnCancel} disabled={isCreating}>
            <Icon name="remove" /> {translate('Cancel')}
          </Button>
          <Button
            color="green"
            onClick={handleCreateRequest}
            disabled={isCreating}
            loading={isCreating}
          >
            <Icon name="check" /> {translate('createRequestBtnLbl')}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default React.memo(KegCollectionCreateModal)
